import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, View } from 'react-native';

import { Pressable } from '../common/Touchable';
import { FRIENDS_FONT, friendsColors, hairline, inset, RADIUS_LG, RADIUS_MD } from './friendsTheme';
import { getInviteCardCopy } from './InviteFriends/inviteFriendsCatalog';
import { IconBack, IconPeople } from './InviteFriends/inviteFriendsIcons';

/**
 * The card at the top of the friends tab that opens Invite Friends. It keeps the Figma "Facebook
 * invite" (4244:41084) card - surface/raised, the modal's hairline, the Heading/M title over a
 * Body/S line - but the Notion spec makes the invite network-agnostic, so the Facebook copy and
 * its blue button give way to the spec's preview: the friends glyph, "Invite Friends", and the
 * whole card as the control.
 */
const INVITE_DESIGN_WIDTH = 334;

const PADDING = 12;
const GAP = 12;
const COPY_GAP = 4;
const ICON_BOX = 52;
const ICON = 32;
const TITLE_SIZE = 18;
const SUBTITLE_SIZE = 12;
const CHEVRON = 16;

type Props = {
	scale: number;
	onInvite?: () => void;
};

export const FriendsInvite = ({ scale, onInvite }: Props) => {
	const styles = useMemo(() => createStyles(scale), [scale]);
	const { t } = useTranslation();
	const copy = useMemo(() => getInviteCardCopy(t), [t]);

	return (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={copy.title}
			onPress={onInvite}
			style={({ pressed }) => [styles.card, pressed && styles.pressed]}
		>
			<View style={styles.icon}>
				<IconPeople size={ICON * scale} />
			</View>

			<View style={styles.copy}>
				<Text style={styles.title}>{copy.title}</Text>
				<Text style={styles.subtitle}>{copy.subtitle}</Text>
			</View>

			{/* The back glyph mirrored - the card leads somewhere. */}
			<View style={styles.chevron}>
				<IconBack size={CHEVRON * scale} color={friendsColors.accent} />
			</View>
		</Pressable>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		card: {
			width: INVITE_DESIGN_WIDTH * scale,
			flexDirection: 'row',
			alignItems: 'center',
			gap: GAP * scale,
			padding: inset(PADDING, scale),
			borderRadius: RADIUS_LG * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.border,
			backgroundColor: friendsColors.surface,
		},
		pressed: {
			borderColor: friendsColors.borderGold,
		},
		icon: {
			width: ICON_BOX * scale,
			height: ICON_BOX * scale,
			alignItems: 'center',
			justifyContent: 'center',
			borderRadius: RADIUS_MD * scale,
			borderWidth: hairline(scale),
			borderColor: friendsColors.borderGold,
			backgroundColor: friendsColors.surfaceSunken,
		},
		copy: {
			flex: 1,
			gap: COPY_GAP * scale,
		},
		// Figma "DS/Heading/M": Sofia Sans Bold 18, line-height 1.3.
		title: {
			color: friendsColors.accent,
			fontFamily: FRIENDS_FONT,
			fontSize: TITLE_SIZE * scale,
			fontWeight: '700',
			lineHeight: TITLE_SIZE * 1.3 * scale,
		},
		// Figma "DS/Body/S": Sofia Sans Medium 12, line-height 1.4.
		subtitle: {
			color: friendsColors.muted,
			fontFamily: FRIENDS_FONT,
			fontSize: SUBTITLE_SIZE * scale,
			fontWeight: '500',
			lineHeight: SUBTITLE_SIZE * 1.4 * scale,
		},
		chevron: {
			transform: [{ scaleX: -1 }],
		},
	});

export default FriendsInvite;
